import { ExtendableError } from '../errors';

export type MaybeInput<T> = Maybe<T> | T | null | undefined;

export class NothingError extends ExtendableError {
    constructor(message?: string | undefined | null) {
        super(message ?? 'NothingError');
    }
}

export default class Maybe<T> implements Iterable<T> {
    private constructor(readonly value: T | null) {}

    static from<T>(input?: MaybeInput<T>): Maybe<T> {
        if (input instanceof Maybe) return input;
        return new Maybe(input ?? null);
    }

    static value<T>(input?: MaybeInput<T>): T | null {
        if (input instanceof Maybe) return input.value;
        return input ?? null;
    }

    // Helpers
    isNothing(): boolean {
        return this.value === null;
    }
    isSome(): boolean {
        return !this.isNothing();
    }
    default<S = T>(val: S): T | S {
        return this.isNothing() ? val : (this.value as T);
    }

    /**
     * Returns the value if Some. Throws an NothingError when nothing.
     *
     * @param nothing The handler for nothing values.
     */
    unpack<S = T>(
        nothing?: Error | string | undefined | null | (() => S)
    ): T | S {
        if (this.value) return this.value;

        if (!nothing || typeof nothing === 'string')
            throw new NothingError(nothing as string | null | undefined);
        if (typeof nothing === 'function') return nothing();

        throw nothing;
    }

    unpackLazy(nothing?: Error | string | null | (() => T)): () => T {
        return () => this.unpack(nothing);
    }

    /**
     * Calls the provided callback only when the value exists.
     *
     * @param onSome
     */
    evaluate(onSome: (val: T) => void): void {
        if (this.value) onSome(this.value);
    }

    merge<S = void>(some: (val: T) => S, nothing: S | (() => S)): S {
        if (this.value) return some(this.value);
        return typeof nothing === 'function' ? (nothing as () => S)() : nothing;
    }

    filter(fn: (val: T) => boolean): Maybe<T> {
        return this.bind((val) => (fn(val) ? val : null));
    }

    *[Symbol.iterator](): Iterator<T, any, undefined> {
        if (this.value) yield this.value;
    }

    get some(): (message?: string) => T {
        return (message?: string) =>
            this.unpack(() => {
                throw new NothingError(message);
            });
    }

    // Monoid behaviour
    static empty<T>() {
        return new Maybe<T>(null);
    }
    static fold<T>(...values: Iterable<MaybeInput<T>>[]): Maybe<T> {
        for (const xs of values) {
            for (const x of xs) {
                const m = Maybe.from(x);
                if (m.isSome()) return m;
            }
        }
        return Maybe.empty();
    }
    append(...others: MaybeInput<T>[]): Maybe<T> {
        return Maybe.fold([this], others);
    }

    // Monad behaviour
    static pure<T>(val: T) {
        return new Maybe<T>(val);
    }
    map<S>(fn: (val: T) => S): Maybe<S> {
        return this.isNothing()
            ? Maybe.empty()
            : new Maybe(fn(this.value as T));
    }
    bind<S>(fn: (val: T) => MaybeInput<S>): Maybe<S> {
        return this.isNothing()
            ? Maybe.empty()
            : Maybe.from(fn(this.value as T));
    }
    async bindAsync<S>(
        fn: (val: T) => Promise<MaybeInput<S>>
    ): Promise<Maybe<S>> {
        if (this.isNothing()) {
            return Maybe.empty();
        } else {
            return Maybe.from(await fn(this.value as T));
        }
    }
}
